import { RoutineWorkflow, useRoutineAlerts } from '@gripp/shared-logic';
import {
  AlertSummary,
  Button,
  Colors,
  LoadingIndicator,
  tokens,
} from '@gripp/shared-ui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';

type AlertsListProps = {
  routineId: string;
  onItemsCountChange?: (count: number) => void;
  /** Override basePath for navigation (e.g. "/products/123/alerts"). Defaults to "/alerts". */
  basePath?: string;
  /** Override productId instead of reading from search params. */
  productId?: string;
  /** Override workspaceAssetId instead of reading from search params. */
  workspaceAssetId?: string;
};

export const AlertsList = ({
  routineId,
  onItemsCountChange,
  basePath: basePathProp,
  productId: productIdProp,
  workspaceAssetId: workspaceAssetIdProp,
}: AlertsListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workspaceAssetId =
    workspaceAssetIdProp ?? searchParams.get('workspaceAssetId') ?? '';
  const productId = productIdProp ?? searchParams.get('productId') ?? undefined;
  const basePath = basePathProp ?? '/alerts';

  const {
    routineWorkflows,
    workspaceAsset,
    isLoading: isRoutineAlertsLoading,
  } = useRoutineAlerts(workspaceAssetId, routineId, productId);

  useEffect(() => {
    if (routineWorkflows) {
      onItemsCountChange?.(routineWorkflows.length);
    }
  }, [routineWorkflows, onItemsCountChange]);

  const buildPath = (suffix: string) => {
    // When using a custom basePath (e.g. product routes), no query params needed
    if (basePathProp) return `${basePath}/${routineId}${suffix}`;
    const params = new URLSearchParams();
    if (workspaceAssetId) params.set('workspaceAssetId', workspaceAssetId);
    if (productId) params.set('productId', productId);
    const qs = params.toString();
    return `${basePath}/${routineId}${suffix}${qs ? `?${qs}` : ''}`;
  };

  const onAlertSelected = (alert: RoutineWorkflow) => {
    const alertId = alert?.id;
    if (!alertId) return;
    navigate(buildPath(`/${alertId}`));
  };

  const handleCreateNew = () => {
    navigate(buildPath('/new'));
  };

  const RenderAlerts = () => {
    return routineWorkflows?.map((item: RoutineWorkflow, index: number) => {
      return (
        <AlertSummary
          onAlertSelected={onAlertSelected}
          key={`${index}-${item.id}`}
          workflow={item}
          workspaceAsset={workspaceAsset}
          routineId={routineId}
        />
      );
    });
  };

  const isLoading = isRoutineAlertsLoading;

  return (
    <View style={styles.container}>
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <LoadingIndicator
            backgroundColor={Colors.role.canvas}
            flexContainer
          />
        </View>
      ) : (
        <View style={styles.contentWrapper}>
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
          >
            <View style={styles.content}>
              <RenderAlerts />
            </View>
          </ScrollView>
          <View style={styles.buttonContainer}>
            <Button
              variant="rectangular"
              size="large"
              state="default"
              onPress={handleCreateNew}
              style={styles.createAlertButton}
            >
              {t('alerts.setANewAlert')}
            </Button>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.role.canvas,
  },
  loadingContainer: {
    flex: 1,
  },
  contentWrapper: {
    flex: 1,
    position: 'relative',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 100, // Space for bottom button
  },
  content: {
    padding: tokens.spacing.xl,
    paddingTop: tokens.spacing.md,
    minHeight: '100%',
  },
  buttonContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: tokens.spacing.xl,
    paddingBottom: tokens.spacing.xl,
    borderTopWidth: 1,
    borderTopColor: Colors.role.borderSubtle,
    backgroundColor: Colors.role.canvas,
  },
  createAlertButton: {
    width: '100%',
  },
});
